"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronDown, Check, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenant, TENANTS, type TenantConfig } from "@/lib/tenant";

// Feature flag to enable/disable tenant switcher dropdown
// Temporarily disabled until multi-tenant launch
export const ENABLE_TENANT_SWITCHER = false;

// Get all tenants as array for the dropdown
const ALL_TENANTS = Object.values(TENANTS);

// Map production domains to local development domains
const DOMAIN_TO_LOCAL: Record<string, string> = {
  "euro-pro-flaeche.de": "euro-pro-flaeche.dev",
  "oekopunktemarkt.de": "oekopunktemarkt.dev",
  "co2-markt.de": "co2-markt.dev",
  "jagdpachtmarkt.de": "jagdpachtmarkt.dev",
  "ee-pachtmarkt.de": "ee-pachtmarkt.dev",
  "ackerpachtmarkt.de": "ackerpachtmarkt.dev"
};

// Tenant logo component - uses image if available, otherwise falls back to icon
function TenantLogo(t0) {
  const $ = _c(25);
  const {
    tenant,
    size: t1
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const sizeClasses = size === "sm" ? "w-8 h-8" : "w-9 h-9";
  const iconSize = size === "sm" ? "w-4 h-4" : "w-5 h-5";
  const imageSize = size === "sm" ? 20 : 24;
  if (tenant.logo.image) {
    let t2;
    if ($[0] !== sizeClasses) {
      t2 = cn(sizeClasses, "rounded-lg flex items-center justify-center overflow-hidden bg-white");
      $[0] = sizeClasses;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== imageSize || $[3] !== tenant.logo.image || $[4] !== tenant.name) {
      t3 = <Image src={tenant.logo.image} alt={tenant.name} width={imageSize} height={imageSize} className="object-contain" />;
      $[2] = imageSize;
      $[3] = tenant.logo.image;
      $[4] = tenant.name;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t2 || $[7] !== t3) {
      t4 = <div className={t2}>{t3}</div>;
      $[6] = t2;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t2;
  if ($[9] !== sizeClasses) {
    t2 = cn(sizeClasses, "rounded-lg flex items-center justify-center");
    $[9] = sizeClasses;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== tenant.theme.primary) {
    t3 = {
      backgroundColor: tenant.theme.primary
    };
    $[11] = tenant.theme.primary;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== tenant.theme.primaryForeground) {
    t4 = {
      color: tenant.theme.primaryForeground
    };
    $[13] = tenant.theme.primaryForeground;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  let t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <path d="M12 2L2 7l10 5 10-5-10-5z" />;
    t6 = <path d="M2 17l10 5 10-5" />;
    t7 = <path d="M2 12l10 5 10-5" />;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
  }
  let t8;
  if ($[18] !== iconSize || $[19] !== t4) {
    t8 = <svg viewBox="0 0 24 24" fill="none" className={iconSize} style={t4} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{t5}{t6}{t7}</svg>;
    $[18] = iconSize;
    $[19] = t4;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t2 || $[22] !== t3 || $[23] !== t8) {
    t9 = <div className={t2} style={t3}>{t8}</div>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
interface TenantSwitcherProps {
  className?: string;
}
export function TenantSwitcher(t0) {
  const $ = _c(31);
  const {
    className
  } = t0;
  const currentTenant = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleClickOutside = function handleClickOutside(event) {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const handleEscape = function handleEscape(event_0) {
        if (event_0.key === "Escape") {
          setIsOpen(false);
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    const isLocalDev = typeof window !== "undefined" && (window.location.hostname.endsWith(".dev") || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
    t5 = tenant => {
      if (typeof window === "undefined") {
        return `https://${tenant.domain}`;
      }
      const protocol = window.location.protocol;
      const port = window.location.port;
      if (isLocalDev) {
        const localDomain = DOMAIN_TO_LOCAL[tenant.domain] || tenant.domain;
        return `${protocol}//${localDomain}${port ? `:${port}` : ""}`;
      }
      return `${protocol}//${tenant.domain}${port ? `:${port}` : ""}`;
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const getTenantUrl = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsOpen(false);
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const handleCurrentTenantClick = t6;
  let t7;
  if ($[6] !== className) {
    t7 = cn("relative", className);
    $[6] = className;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== currentTenant.logo.image || $[9] !== currentTenant.name) {
    t8 = currentTenant.logo.image && <Image src={currentTenant.logo.image} alt={currentTenant.name} width={28} height={28} className="h-7 w-7" />;
    $[8] = currentTenant.logo.image;
    $[9] = currentTenant.name;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== currentTenant.logo.textSuffix) {
    t9 = currentTenant.logo.textSuffix && <span className="text-primary/60">{currentTenant.logo.textSuffix}</span>;
    $[11] = currentTenant.logo.textSuffix;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== currentTenant.logo.text || $[14] !== t9) {
    t10 = <span className="text-[15px]"><span className="text-xl leading-0 font-extrabold tracking-[-0.02em] text-primary">{currentTenant.logo.text}{t9}</span></span>;
    $[13] = currentTenant.logo.text;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = <Link href="/" className="flex items-center gap-2.5 hover:opacity-90 transition-opacity">{t8}{t10}</Link>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== isOpen) {
    t12 = ENABLE_TENANT_SWITCHER && <button onClick={() => setIsOpen(!isOpen)} className="hidden sm:flex p-1 rounded hover:bg-zinc-100 transition-colors" aria-expanded={isOpen} aria-haspopup="listbox" aria-label="Marktplatz wechseln"><ChevronDown className={cn("w-4 h-4 text-zinc-400 transition-transform", isOpen && "rotate-180")} /></button>;
    $[19] = isOpen;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t11 || $[22] !== t12) {
    t13 = <div className="flex items-center gap-2.5 group">{t11}{t12}</div>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== currentTenant.id || $[25] !== isOpen) {
    t14 = ENABLE_TENANT_SWITCHER && isOpen && <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-zinc-200 overflow-hidden z-[100] animate-in fade-in slide-in-from-top-2 duration-200"><div className="px-4 py-3 bg-zinc-50 border-b border-zinc-100"><p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Marktplatz wechseln</p></div><div className="py-2 max-h-[400px] overflow-y-auto" role="listbox">{ALL_TENANTS.map(tenant_0 => {
          const isCurrentTenant = tenant_0.id === currentTenant.id;
          if (isCurrentTenant) {
            return <button key={tenant_0.id} onClick={handleCurrentTenantClick} className="w-full flex items-center gap-3 px-4 py-3 text-left bg-zinc-50" role="option" aria-selected={true}><div className="flex-shrink-0"><TenantLogo tenant={tenant_0} size="sm" /></div><div className="flex-1 min-w-0"><div className="flex items-center gap-2"><span className="font-medium truncate text-zinc-900">{tenant_0.name}</span></div><p className="text-xs text-zinc-500 truncate">{tenant_0.tagline}</p></div><div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: tenant_0.theme.primary
              }}><Check className="w-3 h-3" style={{
                  color: tenant_0.theme.primaryForeground
                }} /></div></button>;
          }
          const tenantUrl = getTenantUrl(tenant_0);
          return <a key={tenant_0.id} href={tenantUrl} className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-50" role="option" aria-selected={false}><div className="flex-shrink-0"><TenantLogo tenant={tenant_0} size="sm" /></div><div className="flex-1 min-w-0"><div className="flex items-center gap-2"><span className="font-medium truncate text-zinc-700">{tenant_0.name}</span><ExternalLink className="w-3 h-3 text-zinc-400 flex-shrink-0" /></div><p className="text-xs text-zinc-500 truncate">{tenant_0.tagline}</p></div></a>;
        })}</div><div className="px-4 py-3 bg-zinc-50 border-t border-zinc-100"><p className="text-xs text-zinc-500">Alle Marktplätze gehören zur <span className="font-medium">Euro Pro Fläche</span>{" "}Familie</p></div></div>;
    $[24] = currentTenant.id;
    $[25] = isOpen;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t13 || $[28] !== t14 || $[29] !== t7) {
    t15 = <div ref={dropdownRef} className={t7}>{t13}{t14}</div>;
    $[27] = t13;
    $[28] = t14;
    $[29] = t7;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}